import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiDelete, apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type {
  Affectation,
  AffectationRequest,
  FlowModel,
  FlowModelRequest,
  Segmentation,
  SegmentationRequest,
} from './types';

// ── Segmentations ───────────────────────────────────────────────────

const segListKey = ['parameters', 'segmentations'] as const;
const segItemKey = (id: string) => ['parameters', 'segmentation', id] as const;

export function useSegmentations(
  options?: Omit<UseQueryOptions<Segmentation[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: segListKey,
    queryFn: () => apiGet<Segmentation[]>(apiPath('alm', 'segmentations')),
    ...options,
  });
}

export function useCreateSegmentation() {
  const qc = useQueryClient();
  return useMutation<Segmentation, CubixApiError, SegmentationRequest>({
    mutationFn: (body) =>
      apiPost<Segmentation, SegmentationRequest>(apiPath('alm', 'segmentations'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: segListKey }),
  });
}

export function useUpdateSegmentation(id: string) {
  const qc = useQueryClient();
  return useMutation<Segmentation, CubixApiError, SegmentationRequest>({
    mutationFn: (body) =>
      apiPut<Segmentation, SegmentationRequest>(apiPath('alm', 'segmentations', id), body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: segListKey });
      qc.invalidateQueries({ queryKey: segItemKey(id) });
    },
  });
}

export function useApproveSegmentation(id: string) {
  const qc = useQueryClient();
  return useMutation<Segmentation, CubixApiError, void>({
    mutationFn: () =>
      apiPut<Segmentation, Record<string, never>>(
        apiPath('alm', 'segmentations', id, 'approve'),
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: segListKey }),
  });
}

export function useArchiveSegmentation(id: string) {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, void>({
    mutationFn: () => apiDelete<void>(apiPath('alm', 'segmentations', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: segListKey }),
  });
}

// ── Flow Models ─────────────────────────────────────────────────────

const fmListKey = ['parameters', 'flow-models'] as const;

export function useFlowModels(
  options?: Omit<UseQueryOptions<FlowModel[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: fmListKey,
    queryFn: () => apiGet<FlowModel[]>(apiPath('alm', 'flow-models')),
    ...options,
  });
}

export function useCreateFlowModel() {
  const qc = useQueryClient();
  return useMutation<FlowModel, CubixApiError, FlowModelRequest>({
    mutationFn: (body) =>
      apiPost<FlowModel, FlowModelRequest>(apiPath('alm', 'flow-models'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: fmListKey }),
  });
}

export function useApproveFlowModel(id: string) {
  const qc = useQueryClient();
  return useMutation<FlowModel, CubixApiError, void>({
    mutationFn: () =>
      apiPut<FlowModel, Record<string, never>>(
        apiPath('alm', 'flow-models', id, 'approve'),
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: fmListKey }),
  });
}

export function useArchiveFlowModel(id: string) {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, void>({
    mutationFn: () => apiDelete<void>(apiPath('alm', 'flow-models', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: fmListKey }),
  });
}

// ── Affectations ────────────────────────────────────────────────────

const affListKey = ['parameters', 'affectations'] as const;

export function useAffectations(
  options?: Omit<UseQueryOptions<Affectation[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: affListKey,
    queryFn: () => apiGet<Affectation[]>(apiPath('alm', 'affectations')),
    ...options,
  });
}

export function useCreateAffectation() {
  const qc = useQueryClient();
  return useMutation<Affectation, CubixApiError, AffectationRequest>({
    mutationFn: (body) =>
      apiPost<Affectation, AffectationRequest>(apiPath('alm', 'affectations'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: affListKey }),
  });
}

export function useUpdateAffectation(id: string) {
  const qc = useQueryClient();
  return useMutation<Affectation, CubixApiError, AffectationRequest>({
    mutationFn: (body) =>
      apiPut<Affectation, AffectationRequest>(apiPath('alm', 'affectations', id), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: affListKey }),
  });
}

export function useApproveAffectation(id: string) {
  const qc = useQueryClient();
  return useMutation<Affectation, CubixApiError, void>({
    mutationFn: () =>
      apiPut<Affectation, Record<string, never>>(
        apiPath('alm', 'affectations', id, 'approve'),
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: affListKey }),
  });
}

export function useArchiveAffectation(id: string) {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, void>({
    mutationFn: () => apiDelete<void>(apiPath('alm', 'affectations', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: affListKey }),
  });
}
